import ExplorerBreadcrumbs from '../../components/Documents/ExplorerBreadcrumbs';
import { ExplorerSearchButton } from '../../components/Documents/ExplorerSearch';
import FileExplorer from '../../components/Documents/FileExplorer';
import MainLayout from '../../layouts/MainLayout';
import React, {
    forwardRef,
    useCallback,
    useEffect,
    useMemo,
    useState,
} from 'react';
import './HomeExplorer.scss';
import {
    getBulkDownload,
    getFolderArchitecture,
    getFolderDownload,
    getFolderNode,
    getMultipleFilesDownload,
} from '../../api/actions/Documents';
import {
    Button,
    Col,
    Container,
    Dropdown,
    DropdownItem,
    DropdownMenu,
    Row,
} from 'react-bootstrap';
import {
    AddOutline,
    CloudUploadOutline,
    DocumentOutline,
    DownloadOutline,
    FolderOutline,
    Reload,
} from 'react-ionicons';
import FileExplorerContext from '../../contexts/FileExplorerContext';
import ExplorerSearchModal from '../../components/Documents/ExplorerSearch';
import CreateFolderModal from '../../components/Documents/Modals/CreateFolderModal';
import UploadElementModal from '../../components/Documents/Modals/UploadElementModal';
import { toast } from 'react-toastify';
import { getOctetsValueFromString } from '../../helpers/Drive';
import { connect } from 'react-redux';
import moment from 'moment';

const FILE_SIZE_LIMIT = 512 * 1000 * 1000;

const CustomUploadDropdown = forwardRef(({ onClick }, ref) => (
    <Button ref={ref} onClick={onClick} className="d-flex align-items-center">
        <CloudUploadOutline
            className="me-2"
            color="white"
            width={'24px'}
            height={'24px'}
        />
        Upload
    </Button>
));

const HomeExplorer = ({ session }) => {
    const [isLoading, setIsLoading] = useState(false);
    const [files, setFiles] = useState([]);
    const [folderArchitecture, setFolderArchitecture] = useState([]);
    const [path, setPath] = useState([]);
    const [currentFolderId, setCurrentFolderId] = useState();
    const [showSearchModal, setShowSearchModal] = useState(false);
    const [showCreateFolderModal, setShowCreateFolderModal] = useState(false);
    const [showUploadModal, setShowUploadModal] = useState();
    const [preUploadFiles, setPreUploadFiles] = useState();
    const [sortBy, setSortBy] = useState('name');
    const [sortOrder, setSortOrder] = useState('ASC');
    const [isFileOver, setIsFileOver] = useState(false);
    const [selectedFiles, setSelectedFiles] = useState([]);

    const fileMaxSize = useMemo(() => {
        if (!session?.modules?.drive?.uploadSizeLimit) {
            return {
                strValue: '512 MB',
                numValue: FILE_SIZE_LIMIT,
            };
        }
        const uploadSizeLimit = session.modules?.drive?.uploadSizeLimit;

        return {
            strValue: uploadSizeLimit,
            numValue: getOctetsValueFromString(uploadSizeLimit),
        };
    }, [session]);

    const multipleDownloadLabel = useMemo(() => {
        let label = 'Download';
        const hasFiles =
            selectedFiles.filter((file) => !file.isDirectory).length > 0;
        const hasFolders =
            selectedFiles.filter((file) => file.isDirectory).length > 0;

        if (hasFiles) {
            label = label + ' files';
        }
        if (hasFiles && hasFolders) {
            label = label + ' &';
        }
        if (hasFolders) {
            label = label + ' folders';
        }

        return label;
    }, [selectedFiles]);

    const onFetchFolderArchitecture = useCallback(async () => {
        try {
            const response = await getFolderArchitecture();

            setFolderArchitecture(response.data.items);
        } catch (error) {
            console.error(error);
        } finally {
            setIsLoading(false);
        }
    }, []);

    const onSortClick = useCallback((sortName, sortOrder, filesToSort) => {
        let sortedFiles = [...filesToSort];

        sortedFiles.sort((a, b) => {
            if (sortName === 'name') {
                if (sortOrder === 'ASC') {
                    if (a.name < b.name) {
                        return -1;
                    } else if (a.name > b.name) {
                        return 1;
                    } else {
                        return 0;
                    }
                } else {
                    if (a.name > b.name) {
                        return -1;
                    } else if (a.name < b.name) {
                        return 1;
                    } else {
                        return 0;
                    }
                }
            }
            if (sortName === 'lastUpdate') {
                if (sortOrder === 'DESC') {
                    if (a.isDirectory && b.isDirectory) {
                        return 0;
                    }
                    if (a.isDirectory && !b.isDirectory) {
                        return 1;
                    }

                    if (!a.isDirectory && b.isDirectory) {
                        return -1;
                    }
                    const date1 = moment(a.file.updatedAt);
                    const date2 = moment(b.file.updatedAt);
                    return date1.diff(date2);
                } else {
                    if (a.isDirectory && b.isDirectory) {
                        return 0;
                    }
                    if (a.isDirectory && !b.isDirectory) {
                        return 1;
                    }

                    if (!a.isDirectory && b.isDirectory) {
                        return -1;
                    }
                    const date1 = moment(a.file.updatedAt);
                    const date2 = moment(b.file.updatedAt);
                    return date2.diff(date1);
                }
            }

            return 0;
        });

        setFiles(sortedFiles);
        setSortOrder(sortOrder);
        setSortBy(sortName);
    }, []);

    const onOpenFolder = useCallback(
        async (folderId) => {
            setIsLoading(true);

            try {
                const response = await getFolderNode(folderId, 1);

                setFiles(response.data.structure);
                setPath(response.data.fullPath);
                setCurrentFolderId(folderId);
                if (sortBy && sortOrder) {
                    onSortClick(sortBy, sortOrder, response.data.structure);
                }
            } catch (error) {
                console.error(error);
                toast.error(
                    'Something wrong happened while trying to open the folder. Please try again.',
                    {
                        className: 'custom-toast',
                    }
                );
            } finally {
                setIsLoading(false);
            }
        },
        [sortBy, sortOrder, onSortClick]
    );

    useEffect(() => {
        onFetchFolderArchitecture();
        onOpenFolder('');
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const toggleSearchModal = useCallback(() => {
        setShowSearchModal((prev) => !prev);
    }, []);

    const toggleCreateFolderModal = useCallback(() => {
        setShowCreateFolderModal((prev) => !prev);
    }, []);

    const toggleUploadModal = useCallback(
        (uploadType) => {
            if (preUploadFiles && !uploadType) {
                setPreUploadFiles(undefined);
            }
            setShowUploadModal(uploadType);
        },
        [preUploadFiles]
    );

    const onRefresh = useCallback(() => {
        onOpenFolder(currentFolderId ?? '');
    }, [onOpenFolder, currentFolderId]);

    const readDirectory = useCallback(
        (entry, newFiles) => {
            const dirReader = entry.createReader();
            dirReader.readEntries((entries) => {
                entries.forEach((subEntry) => {
                    if (subEntry.isFile) {
                        subEntry.file((file) => {
                            if (file.size > fileMaxSize.numValue) {
                                toast.error(
                                    `File ${file.name} is above the limit of ${fileMaxSize.strValue}.`,
                                    {
                                        className: 'custom-toast',
                                    }
                                );
                            } else {
                                const item = {
                                    file,
                                    webkitRelativePath: `${entry.fullPath.substring(
                                        1
                                    )}/${file.name}`,
                                };

                                setPreUploadFiles((prevFiles) =>
                                    prevFiles ? [...prevFiles, item] : [item]
                                );
                            }
                        });
                    } else if (subEntry.isDirectory) {
                        readDirectory(subEntry, newFiles);
                    }
                });
            });
        },
        [fileMaxSize]
    );

    const onDropFile = useCallback(
        (e) => {
            e.preventDefault();
            setIsFileOver(false);
            const items = e.dataTransfer.items;
            const newFiles = [];

            for (let i = 0; i < items.length; i++) {
                const item = items[i];
                const entry = item.webkitGetAsEntry();

                if (entry) {
                    if (entry.isFile) {
                        entry.file((file) => {
                            if (file.size > fileMaxSize.numValue) {
                                toast.error(
                                    `File ${file.name} is above the limit of ${fileMaxSize.strValue}.`,
                                    {
                                        className: 'custom-toast',
                                    }
                                );
                            } else {
                                newFiles.push(file);
                                setPreUploadFiles((prevFiles) =>
                                    prevFiles ? [...prevFiles, file] : [file]
                                );
                            }
                        });
                    } else if (entry.isDirectory) {
                        readDirectory(entry, newFiles);
                    }
                }
            }

            if (items.length > 0 && items[0].webkitGetAsEntry().isDirectory) {
                setShowUploadModal('folder');
            } else {
                setShowUploadModal('file');
            }
        },
        [readDirectory, fileMaxSize]
    );

    const onDrag = useCallback((event) => {
        event.preventDefault();
    }, []);

    const onAllFilesChecked = useCallback(() => {
        if (selectedFiles.length === files.length) {
            setSelectedFiles([]);
        } else {
            setSelectedFiles(files);
        }
    }, [files, selectedFiles]);

    const onFileChecked = useCallback(
        (item) => {
            if (selectedFiles.map((file) => file.id).includes(item.id)) {
                const newArr = selectedFiles.filter(
                    (file) => file.id !== item.id
                );

                setSelectedFiles(newArr);
            } else {
                setSelectedFiles([...selectedFiles, item]);
            }
        },
        [selectedFiles]
    );

    const onDownload = useCallback(
        async (e) => {
            if (e && e.preventDefault) {
                e.preventDefault();
                e.stopPropagation();
                e.target.blur();
            }

            try {
                const requestBody = {
                    folderIds: selectedFiles
                        .filter((file) => file.isDirectory)
                        .map((file) => file.id),
                    fileIds: selectedFiles
                        .filter((file) => !file.isDirectory)
                        .map((file) => file.id),
                };

                const response = await fetch(getBulkDownload(), {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify(requestBody),
                });

                if (!response.ok) {
                    throw new Error('Folders download failed');
                }

                const blob = await response.blob();
                const url = window.URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.download = 'documents.zip';
                link.href = url;
                link.click();
                window.URL.revokeObjectURL(url);
            } catch (error) {
                console.error('Error downloading file:', error);
                toast.error(
                    'Something wrong happened while downloading the files. Please try again.'
                );
            }
        },
        [selectedFiles]
    );

    return (
        <FileExplorerContext.Provider
            value={{
                onOpenFolder,
                fullPath: path,
                fetchFolderArchitecture: onFetchFolderArchitecture,
                folderArchitecture,
            }}
        >
            <MainLayout pageTitle={'Documents'}>
                <Container fluid onDragOver={onDrag} onDrop={onDropFile}>
                    <Row>
                        <Col
                            sm={12}
                            className="d-flex align-items-center justify-content-between"
                        >
                            <h2>File Explorer</h2>
                            <div className="d-flex align-items-center gap-2">
                                <Button
                                    onClick={toggleCreateFolderModal}
                                    className="d-flex align-items-center"
                                >
                                    <AddOutline
                                        className="me-2"
                                        color="white"
                                        width={'24px'}
                                        height={'24px'}
                                    />
                                    Create folder
                                </Button>
                                <Dropdown drop="start" align={{ lg: 'start' }}>
                                    <Dropdown.Toggle as={CustomUploadDropdown}>
                                        Upload
                                    </Dropdown.Toggle>
                                    <DropdownMenu>
                                        <DropdownItem
                                            as="button"
                                            onClick={() =>
                                                setShowUploadModal('file')
                                            }
                                            className="d-flex align-items-center"
                                        >
                                            <DocumentOutline
                                                className="me-2"
                                                style={{ marginBottom: 2 }}
                                            />
                                            Import a file
                                        </DropdownItem>
                                        <DropdownItem
                                            as="button"
                                            onClick={() =>
                                                setShowUploadModal('folder')
                                            }
                                            className="d-flex align-items-center"
                                        >
                                            <FolderOutline
                                                className="me-2"
                                                style={{ marginBottom: 2 }}
                                            />
                                            Import a folder
                                        </DropdownItem>
                                    </DropdownMenu>
                                </Dropdown>
                            </div>
                        </Col>
                    </Row>
                    <Row>
                        <Col>
                            <div
                                className="d-flex justify-content-between align-items-center bg-white rounded shadow my-4 py-2"
                                style={{ paddingLeft: 8, paddingRight: 8 }}
                            >
                                <ExplorerBreadcrumbs path={path} />
                                <div className="d-flex align-items-center gap-2">
                                    {selectedFiles.length > 0 && (
                                        <Dropdown>
                                            <Dropdown.Toggle>
                                                Multiple Actions
                                            </Dropdown.Toggle>
                                            <Dropdown.Menu>
                                                <Dropdown.Item
                                                    className="d-flex align-items-center"
                                                    as="button"
                                                    onClick={onDownload}
                                                >
                                                    <DownloadOutline
                                                        className="me-2"
                                                        style={{
                                                            marginBottom: 2,
                                                        }}
                                                    />
                                                    {multipleDownloadLabel}
                                                </Dropdown.Item>
                                            </Dropdown.Menu>
                                        </Dropdown>
                                    )}
                                    <Button onClick={onRefresh}>
                                        <Reload color="white" />
                                    </Button>
                                    <ExplorerSearchButton
                                        onClick={toggleSearchModal}
                                    />
                                </div>
                            </div>
                        </Col>
                    </Row>
                    <Row>
                        <Col>
                            <FileExplorer
                                onSortClick={(name, order) =>
                                    onSortClick(name, order, files)
                                }
                                isLoading={isLoading}
                                files={files}
                                selectedFiles={selectedFiles}
                                onAllFilesChecked={onAllFilesChecked}
                                onFileChecked={onFileChecked}
                                isFileOver={isFileOver}
                            />
                        </Col>
                    </Row>
                </Container>
                <ExplorerSearchModal
                    show={showSearchModal}
                    onClose={toggleSearchModal}
                />
                <CreateFolderModal
                    show={showCreateFolderModal}
                    onClose={toggleCreateFolderModal}
                />
                <UploadElementModal
                    show={showUploadModal ? true : false}
                    onClose={toggleUploadModal}
                    preUploadFiles={preUploadFiles}
                    uploadType={showUploadModal}
                />
            </MainLayout>
        </FileExplorerContext.Provider>
    );
};

const mapStateToProps = (appState) => ({
    session: appState.session,
});

export default connect(mapStateToProps)(HomeExplorer);
